import type { EventJson, SubscriptionJson } from '../gen/creativefabrica/notifications/v1/types_pb'
import { getNotificationsBaseUrl, sessionId } from './common'

import { fetchRpc } from '@repo/rpc-codegen/fetch-rpc'

/**
 * A token stream registers with the same JWT the stream carries so the backend sees one identity
 * for both. The cookie stream has no token: the cookie authenticates both requests at the gateway
 * and `fetchRpc` adds no bearer of its own there.
 */
function authHeaders(token: string | undefined): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * Subscribes to an event via the notifications service
 * @param event - The event to subscribe to
 * @param controller - AbortController for cancelling the request
 * @param token - JWT of a token stream; omit on the cookie stream
 * @returns Promise with subscription response
 */
// oxlint-disable-next-line require-await
export async function subscribe(
  event: EventJson,
  controller: AbortController,
  token: string | undefined,
): Promise<{ subscriptions?: SubscriptionJson[] }> {
  return fetchRpc<{ subscriptions?: SubscriptionJson[] }>({
    url: `${getNotificationsBaseUrl()}/creativefabrica.notifications.v1.NotificationsService/Subscribe`,
    variables: {
      sessionId,
      events: [event],
    },
    controller,
    headers: authHeaders(token),
  })
}

/**
 * Unsubscribes from events via the notifications service
 * @param subscriptionIds - Array of subscription IDs to unsubscribe from
 * @param controller - AbortController for cancelling the request
 * @param token - JWT of a token stream; omit on the cookie stream
 * @returns Promise with unsubscription response
 */
// oxlint-disable-next-line require-await
export async function unsubscribe(
  subscriptionIds: string[],
  controller: AbortController,
  token: string | undefined,
): Promise<Record<string, never>> {
  return fetchRpc<Record<string, never>>({
    url: `${getNotificationsBaseUrl()}/creativefabrica.notifications.v1.NotificationsService/Unsubscribe`,
    variables: { sessionId, ids: subscriptionIds },
    controller,
    headers: authHeaders(token),
  })
}
